import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Learn how FindOnlineDegrees collects, uses, protects, and shares personal information when you use our website and services.'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1830" data-mp="2rs40opi912qi">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-1831" data-mp="2e17992026rl1">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1832" data-mp="2dzw2quf8b5q8">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-1833" data-mp="2it8tpb7drom5">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-1834" data-mp="244vwkawk3zq2">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-1835" data-mp="290b30hs8itcg">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-1836" data-mp="1v45z72rne1a8">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1837" data-mp="1z4pzgf7ipl0l">
            <p data-id="element-1838" data-mp="29rmqxa447a00">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-1839" data-mp="2johrdyjy7kgx">
              <li data-id="element-1840" data-mp="2eqpwqwrx5k91">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-1841" data-mp="1r1x0ez7dxadw">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-1842" data-mp="20rgjo5z0f69a">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-1843" data-mp="13basr91als4f">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1844" data-mp="139q7yb4s5njr">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1845" data-mp="1x11sye27ir2y">
              <ul className="list-disc pl-6 space-y-2" data-id="element-1846" data-mp="1wz744onteetl">
                <li data-id="element-1847" data-mp="21t1n1pvliprm">
                  <strong data-id="element-1848" data-mp="21sjscxol0jlt">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-1849" data-mp="1e4if5bggkf02">
                  <strong data-id="element-1850" data-mp="1j1sb79ycrowv">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-1851" data-mp="1nyirnye2kmit">
                  <strong data-id="element-1852" data-mp="2ugnzzoqpr6r5">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-1853" data-mp="nu9kos3kvqn0">
                  <strong data-id="element-1854" data-mp="itlw0d2tnlvy">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-1855" data-mp="52groxoie5fa">
                  <strong data-id="element-1856" data-mp="52yu6c1rlfvn">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-1857" data-mp="3maat78gilj8y">
                  <strong data-id="element-1858" data-mp="3mau80gavbrir">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-1859" data-mp="1kjqzpxogap1d">
                  <strong data-id="element-1860" data-mp="1kj7eo83i7kj4">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-1861" data-mp="1xiqas5ngnvd2">
                  <strong data-id="element-1862" data-mp="1xhygqz9ii5ow">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-1863" data-mp="19t5u5tes5a6y">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1864" data-mp="19smaa75kkbqa">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1865" data-mp="1eknrm7hlgsn6">
              <li data-id="element-1866" data-mp="zv85x5wx7dec">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-1867" data-mp="zwj8x3ijkh4e">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-1868" data-mp="14szzs5h4l9l6">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-1869" data-mp="14rgg04w9ggen">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-1870" data-mp="quthvysknk75">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-1871" data-mp="uuk8a1e6ryb5">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-1872" data-mp="c7b7qkw8pxdc">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-1873" data-mp="c7l2smw56upw">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-1874" data-mp="79026mcza90x">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-1875" data-mp="7ab6qoe8zkt3">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-1876" data-mp="l92elt2ioa8s">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-1877" data-mp="g8wju40ya7mh">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-1878" data-mp="v4w1a59y9k4a">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1879" data-mp="v4m6m4dymos9">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1880" data-mp="13yhevladovc4">
              <h3 className="font-bold text-lg" data-id="element-1881" data-mp="13xpi4751ork9">
                Personal information you disclose to us
              </h3>
              <p data-id="element-1882" data-mp="1dwoi71bqvevn">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-1883" data-mp="akzn0olp53pg">
                <strong data-id="element-1884" data-mp="amkhyfbxaonu">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1885" data-mp="kbkhbzuc7m2k">
                <li data-id="element-1886" data-mp="kd59xjx3cuxs">Names</li>
                <li data-id="element-1887" data-mp="kdf5ro4thwbe">Phone numbers</li>
                <li data-id="element-1888" data-mp="pbgjrundz5g0">Email addresses</li>
                <li data-id="element-1889" data-mp="padyc28u1v7h">Mailing addresses</li>
                <li data-id="element-1890" data-mp="u9i7isvrbsxt">Education level and interests</li>
                <li data-id="element-1891" data-mp="u7xamwlyzxws">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1892" data-mp="yb3lkhk12d9l">
                Information automatically collected
              </h3>
              <p data-id="element-1893" data-mp="139wsts8q181b">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1894" data-mp="2xjvag5p6xfdz">
                <li data-id="element-1895" data-mp="2sr21t6qlpmwm">IP address</li>
                <li data-id="element-1896" data-mp="2spzdn6knxyke">Browser and device characteristics</li>
                <li data-id="element-1897" data-mp="2nrxtsjfexv7v">Operating system</li>
                <li data-id="element-1898" data-mp="2nt0iqlt6dnl5">Language preferences</li>
                <li data-id="element-1899" data-mp="2itnym8touum9">Referring URLs</li>
                <li data-id="element-1900" data-mp="2iupagfij3l51">Device name and type</li>
                <li data-id="element-1901" data-mp="2irjld09wgj9b">Pages viewed and time spent</li>
                <li data-id="element-1902" data-mp="3gg3cl0mwpa52">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1903" data-mp="3bg8m2y71mmkj">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1904" data-mp="36ip0fd80n7ik">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1905" data-mp="32glnek5p7uv6">
              <p data-id="element-1906" data-mp="1v4bhmms4di9e">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1907" data-mp="1r0wyvocsqns9">
                <li data-id="element-1908" data-mp="3q6ghd24qpmak">To facilitate account creation and authentication</li>
                <li data-id="element-1909" data-mp="3v61inj7du6qg">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1910" data-mp="3v3yiw7gfgz8j">To respond to user inquiries and offer support</li>
                <li data-id="element-1911" data-mp="3h7lcpuhcgk9d">To send administrative information to you</li>
                <li data-id="element-1912" data-mp="3m4uzf2dcmhti">To fulfill and manage your requests</li>
                <li data-id="element-1913" data-mp="3m7h7d345izc9">To enable user-to-user communications</li>
                <li data-id="element-1914" data-mp="dqmfsjst11dh">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1915" data-mp="hszj71k2wn73">To send you marketing and promotional communications</li>
                <li data-id="element-1916" data-mp="3z8hgfcla7lx">To deliver targeted advertising to you</li>
                <li data-id="element-1917" data-mp="8y1nxntgkycl">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1918" data-mp="wnnly7y3h7j0">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1919" data-mp="1atxw8rv6ewej">To save or protect an individual's vital interest</li>
                <li data-id="element-1920" data-mp="1as4mh3p9nw2b">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1921" data-mp="16ruke24lhpsq">Email Communications</h3>
              <p data-id="element-1922" data-mp="1kk0rkyprsmg1">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1923" data-mp="28bpjwf2ldawz">
                Advertising Pixels and Third-Party Measurement
              </h3>
              <p data-id="element-1924" data-mp="1bk2b800bom9c">
                We use pixels, web beacons, cookies, and other storage
                technologies from third parties, including Meta (Facebook), to
                collect or receive information from our websites and elsewhere
                on the internet. Those third parties, including Meta, use that
                information to provide measurement services, and to target and
                deliver advertising. This may include sharing a hashed
                identifier and information about the actions you take on our
                site — such as viewing a page or submitting a request for
                information — so that advertising can be measured and
                attributed. We do not share the substance of any sensitive
                information for advertising purposes.
              </p>
              <p data-id="element-1925" data-mp="2daharotogqsd">
                You can opt out of the collection and use of this information
                for ad targeting at{' '}
                <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-1926" data-mp="2ps1sfoctyrxj">
                  aboutads.info/choices
                </a>{' '}
                (US) or{' '}
                <a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-1927" data-mp="22t5h2kmhno8y">
                  youronlinechoices.eu
                </a>{' '}
                (EU), and manage Meta ad preferences at{' '}
                <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-1928" data-mp="3n8gz7g1vowud">
                  facebook.com/settings?tab=ads
                </a>
                . Our{' '}
                <a href="/cookie-policy" className="text-[#3b82f6] underline hover:no-underline" data-id="element-1929" data-mp="2xlmkqalswa40">
                  Cookie Policy
                </a>{' '}
                explains these technologies in more detail.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1930" data-mp="2eudf4cf5i5hp">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1931" data-mp="2euvgfngo79wh">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1932" data-mp="2ori1ac8zy0yi">
              <h3 className="font-bold text-lg" data-id="element-1933" data-mp="2op5b5bt34kad">How We Collect SMS Consent</h3>
              <p data-id="element-1934" data-mp="2102vg9i0h1ii">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1935" data-mp="uaqdwkzjxzst">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1936" data-mp="z9ksfaqhl274">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1937" data-mp="pbn7aw3xbliq">
                <li data-id="element-1938" data-mp="pa3ph12md2eu">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1939" data-mp="362x0b8ka84zl">Follow-up communications related to your inquiry</li>
                <li data-id="element-1940" data-mp="3a3gxgc4uv8lq">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1941" data-mp="3lhitht5agfqw">
                Consent Is Not Required
              </h3>
              <p data-id="element-1942" data-mp="3gk0k61nczyid">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1943" data-mp="83e1z08xenq1">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1944" data-mp="339kb6wa7my8">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1945" data-mp="2nspbi2zexdoe">How to Opt Out</h3>
              <p data-id="element-1946" data-mp="2jrn7z9ygqfgm">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1947" data-mp="13cr8ci047nx4">
                <li data-id="element-1948" data-mp="13dalzmiqfq5c">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1949" data-mp="18asy1ls1qlhk">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1950" data-mp="1d4niirfh74hp">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1951" data-mp="1r2v9miptzbky">For Assistance</h3>
              <p data-id="element-1952" data-mp="1w1opuiwi5got">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1953" data-mp="1mvt2gjvujva">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1954" data-mp="14xjgaxo7oonz">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1955" data-mp="1nw5fu842y7ps">Record Retention</h3>
              <p data-id="element-1956" data-mp="1iyc99dyv7boz">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1957" data-mp="26o6qe7q7vmf1">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1958" data-mp="26lkiu83bt6wt">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1959" data-mp="2lbf53iiqi9vr">
              <p data-id="element-1960" data-mp="2q96y6fv4821j">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1961" data-mp="2q745fo2kvut4">
                <li data-id="element-1962" data-mp="2bbefxfy6ozjn">
                  <strong data-id="element-1963" data-mp="2bby0z5j4s41w">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1964" data-mp="22i1etqpyurmh">
                  <strong data-id="element-1965" data-mp="27acin4ntuxkb">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1966" data-mp="1xk9p93csnhlg">
                  <strong data-id="element-1967" data-mp="1xlks90yf0lbi">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1968" data-mp="1nlbu3hkv4pth">
                  <strong data-id="element-1969" data-mp="1nopuwldvoqyt">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1970" data-mp="1z2i42nokc72i">
                  <strong data-id="element-1971" data-mp="1z42txzzxn1u1">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1972" data-mp="1l39zff9ms6qx">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1973" data-mp="1l30azw48i7nc">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1974" data-mp="1gb8qo3bo8y5n">
              <p data-id="element-1975" data-mp="1beiazh3s44kr">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1976" data-mp="xez38pevqv8z">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1977" data-mp="xehba4z9336v">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1978" data-mp="3d2crvdv4wgfn">
              <p data-id="element-1979" data-mp="3d4pi0eb1px3s">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1980" data-mp="2za6mmmtirtuz">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1981" data-mp="2z9n7fdv97jkn">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1982" data-mp="26mk8wlqkv8ue">
              <p data-id="element-1983" data-mp="26mal94t09drv">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1984" data-mp="1ygqkyqggixuz">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1985" data-mp="1yf5qt1y21gxn">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1986" data-mp="10qvlacz28b1s">
              <p data-id="element-1987" data-mp="15sb6c518mhk6">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1988" data-mp="30bv0erfypcac">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1989" data-mp="30aa692xk7vd0">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1990" data-mp="2w7vjw8uomdgz">
              <p data-id="element-1991" data-mp="2r9ltebjwk11p">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1992" data-mp="2mbk8rm6tvto4">
                <li data-id="element-1993" data-mp="2hcz5tf06yvvz">The right to access your personal information</li>
                <li data-id="element-1994" data-mp="2cm0piff7dxsq">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1995" data-mp="28jvxfl4el987">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1996" data-mp="23mdk7ijcs7ue">The right to opt out of marketing communications</li>
                <li data-id="element-1997" data-mp="1ymr7rbbcmvmn">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1998" data-mp="1jzisnzex665e">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1999" data-mp="1ozx0bgclze2w">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-2000" data-mp="1b0x9q35jcp43">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2001" data-mp="27r7nbmf7r6pi">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-2002" data-mp="1xys6isfnko67">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-2003" data-mp="a19b4bqdncth">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2004" data-mp="9zoor9e7v06p">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2005" data-mp="jr2it1nef7sg">
              <p data-id="element-2006" data-mp="3s75tlg9t5cl0">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-2007" data-mp="1hdrszys98qpe">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2008" data-mp="1hdjigdjltxhd">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2009" data-mp="3i4a8llj9qwlx">
              <p data-id="element-2010" data-mp="3i64v34a6uws4">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-2011" data-mp="4ya3h6ufe0mm">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2012" data-mp="4yjrwpztnzq7">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2013" data-mp="9uqp9ue5k2wj">
              <p data-id="element-2014" data-mp="eouy6j6zal09">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-2015" data-mp="no9o9hj8gtk8">Media Matchers LLC</p>
              <p data-id="element-2016" data-mp="npt63ckjfco4">
                1865 Chestnut Street
                <br data-id="element-2017" data-mp="smtalj1qo7d4" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-2018" data-mp="12r9pa93hfifc">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-2019" data-mp="12s2p0q40x7nz">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}